import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'
import { createInitialReturnRequest, type ReturnRequest, type ReturnPortal } from '~/model'

export const useReturnFormReset = (returnPortal: ReturnPortal) => {
  const { t, i18n } = useTranslation()
  const { values: returnRequest, setValues } = useFormikContext<ReturnRequest>()
  const {
    address: { countryCode },
    transportType,
    returnType,
  } = returnRequest

  const setFormValues = useCallback(
    (newValues: Partial<ReturnRequest>) => {
      setValues((values) => ({
        ...values,
        ...newValues,
      }))
    },
    [setValues]
  )

  // When a country is selected, reset the complete form
  useEffect(() => {
    const init = createInitialReturnRequest({
      returnPortal,
      currentCountryCode: countryCode.key,
      language: i18n.language,
      t,
    })
    setFormValues({
      address: { ...init.address, countryCode },
      transportType: init.transportType,
      returnType: init.returnType,
      returnDeclarationMechanism: init.returnDeclarationMechanism,
      pickupDate: init.pickupDate,
      pickupTimeWindowStart: init.pickupTimeWindowStart,
      pickupTimeWindowEnd: init.pickupTimeWindowEnd,
      packageSizeConfirmed: init.packageSizeConfirmed,
      pallets: init.pallets,
      packages: init.packages,
    })
    // eslint-disable-next-line
  }, [countryCode, setFormValues, returnPortal])

  // When transport type is changed, reset date/time and calculate/set the defaults for return/label type
  useEffect(() => {
    const init = createInitialReturnRequest({
      returnPortal,
      currentCountryCode: countryCode.key,
      currentTransportType: transportType,
      language: i18n.language,
      t,
    })
    setFormValues({
      returnType: init.returnType,
      returnDeclarationMechanism: init.returnDeclarationMechanism,
      pickupDate: init.pickupDate,
      pickupTimeWindowStart: init.pickupTimeWindowStart,
      pickupTimeWindowEnd: init.pickupTimeWindowEnd,
      packageSizeConfirmed: init.packageSizeConfirmed,
      pallets: init.pallets,
      packages: init.packages,
    })
    // eslint-disable-next-line
  }, [transportType, setFormValues, returnPortal])

  // When return type is changed, reset date/time
  useEffect(() => {
    const init = createInitialReturnRequest({
      returnPortal,
      language: i18n.language,
      t,
    })
    setFormValues({
      pickupDate: init.pickupDate,
      pickupTimeWindowStart: init.pickupTimeWindowStart,
      pickupTimeWindowEnd: init.pickupTimeWindowEnd,
      packageSizeConfirmed: init.packageSizeConfirmed,
    })
    // eslint-disable-next-line
  }, [returnType, setFormValues, returnPortal])
}
